import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState, useId, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
	verifyPasswordResetCode,
	confirmPasswordReset,
	getAuth,
} from "firebase/auth";
import { captureAuthException } from "@/lib/auth/captureAuthException";

type ResetState = "verifying" | "enterPassword" | "success" | "error";

/**
 * Password Reset Page
 *
 * Handles the Firebase password reset flow using the oobCode from the email link.
 * Based on Firebase's custom email handler documentation:
 * https://firebase.google.com/docs/auth/custom-email-handler
 *
 * Flow:
 * 1. Verify the oobCode is valid
 * 2. Show the user's email and password input form
 * 3. Confirm the password reset with Firebase
 * 4. Show success message and redirect to login
 */
const PasswordResetPage = () => {
	const { t } = useTranslation("common");
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	// Generate unique IDs for form inputs
	const emailId = useId();
	const passwordId = useId();
	const confirmPasswordId = useId();

	const [state, setState] = useState<ResetState>("verifying");
	const [oobCode, setOobCode] = useState<string | null>(null);
	const [email, setEmail] = useState<string>("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	/**
	 * Verify the password reset code with Firebase
	 */
	const verifyResetCode = useCallback(
		async (code: string) => {
			try {
				const auth = getAuth();
				// Verify the password reset code is valid and get the user's email
				const userEmail = await verifyPasswordResetCode(auth, code);
				setEmail(userEmail);
				setState("enterPassword");
			} catch (error) {
				console.error("Failed to verify reset code:", error);
				captureAuthException(error, "password_reset_verify_code");
				setState("error");

				// Provide specific error messages based on Firebase error codes
				const firebaseError = error as { code?: string };
				if (firebaseError.code === "auth/expired-action-code") {
					setError(t("passwordReset.expiredLink"));
				} else if (firebaseError.code === "auth/invalid-action-code") {
					setError(t("passwordReset.invalidLink"));
				} else {
					setError(t("passwordReset.verificationFailed"));
				}
			}
		},
		[t],
	);

	// Extract and verify the oobCode on mount
	useEffect(() => {
		const code = searchParams.get("oobCode");
		const mode = searchParams.get("mode");

		// Validate that we have the correct parameters
		if (!code || mode !== "resetPassword") {
			setState("error");
			setError(t("passwordReset.invalidLink"));
			return;
		}

		setOobCode(code);
		verifyResetCode(code);
	}, [searchParams, t, verifyResetCode]);

	/**
	 * Handle password reset form submission
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// Validate passwords
		if (password.length < 6) {
			setError(t("passwordReset.passwordTooShort"));
			return;
		}

		if (password !== confirmPassword) {
			setError(t("passwordReset.passwordMismatch"));
			return;
		}

		if (!oobCode) {
			setError(t("passwordReset.invalidLink"));
			return;
		}

		setIsLoading(true);

		try {
			const auth = getAuth();
			// Confirm the password reset with Firebase
			await confirmPasswordReset(auth, oobCode, password);
			setState("success");

			// Redirect to login after 3 seconds
			setTimeout(() => {
				navigate("/auth/login");
			}, 3000);
		} catch (error) {
			console.error("Failed to reset password:", error);
			captureAuthException(error, "password_reset_confirm");

			// Provide specific error messages
			const firebaseError = error as { code?: string };
			if (firebaseError.code === "auth/weak-password") {
				setError(t("passwordReset.weakPassword"));
			} else if (firebaseError.code === "auth/expired-action-code") {
				setError(t("passwordReset.expiredLink"));
			} else if (firebaseError.code === "auth/invalid-action-code") {
				setError(t("passwordReset.invalidLink"));
			} else {
				setError(t("passwordReset.resetFailed"));
			}
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Render loading state while verifying the code
	 */
	if (state === "verifying") {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8 text-center">
					<div className="flex justify-center">
						<PodhiveLogo width={125} bildmarke={false} />
					</div>
					<div className="space-y-4">
						<Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
						<p className="text-muted-foreground">
							{t("passwordReset.verifying")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render error state if verification failed
	 */
	if (state === "error") {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8">
					<div className="flex justify-center">
						<PodhiveLogo width={125} bildmarke={false} />
					</div>

					<div className="rounded-lg bg-destructive/10 border-2 border-destructive/50 p-6 text-center space-y-4">
						<XCircle className="h-12 w-12 mx-auto text-destructive" />
						<h1 className="text-xl font-semibold text-destructive">
							{t("passwordReset.errorTitle")}
						</h1>
						<p className="text-sm text-destructive/90">{error}</p>
					</div>

					<div className="space-y-4">
						<Button
							onClick={() => navigate("/auth/login")}
							className="w-full"
							size="lg"
						>
							{t("passwordReset.backToLogin")}
						</Button>

						<div className="text-center text-sm text-muted-foreground">
							{t("passwordReset.needNewLink")}
						</div>
					</div>

					<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
						<Link to="/agb">{t("footer.terms")}</Link>
						<Link to="/datenschutz">{t("footer.privacy")}</Link>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render success state after password reset
	 */
	if (state === "success") {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
				<div className="w-full max-w-[400px] space-y-8">
					<div className="flex justify-center">
						<PodhiveLogo width={125} bildmarke={false} />
					</div>

					<div className="rounded-lg bg-green-500/10 border-2 border-green-500/50 p-6 text-center space-y-4">
						<CheckCircle2 className="h-12 w-12 mx-auto text-green-600 dark:text-green-400" />
						<h1 className="text-xl font-semibold text-green-700 dark:text-green-300">
							{t("passwordReset.successTitle")}
						</h1>
						<p className="text-sm text-green-600 dark:text-green-400">
							{t("passwordReset.successMessage")}
						</p>
					</div>

					<Button
						onClick={() => navigate("/auth/login")}
						className="w-full"
						size="lg"
					>
						{t("passwordReset.goToLogin")}
					</Button>

					<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
						<Link to="/agb">{t("footer.terms")}</Link>
						<Link to="/datenschutz">{t("footer.privacy")}</Link>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render password reset form
	 */
	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
			<div className="w-full max-w-[400px] space-y-8">
				<div className="flex justify-center">
					<PodhiveLogo width={125} bildmarke={false} />
				</div>

				<div className="space-y-2 text-center">
					<h1 className="text-2xl font-semibold text-foreground">
						{t("passwordReset.title")}
					</h1>
					<p className="text-sm text-muted-foreground">
						{t("passwordReset.subtitle")}
					</p>
				</div>

				{error && (
					<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
						{error}
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-6">
					{/* Email Display (read-only) */}
					<div className="space-y-2">
						<label className="text-sm text-muted-foreground" htmlFor={emailId}>
							{t("email")}
						</label>
						<Input
							id={emailId}
							type="email"
							value={email}
							disabled
							className="w-full"
						/>
					</div>

					{/* New Password Input */}
					<div className="space-y-2">
						<label
							className="text-sm text-muted-foreground"
							htmlFor={passwordId}
						>
							{t("passwordReset.newPassword")}
						</label>
						<div className="relative">
							<Input
								id={passwordId}
								type={showPassword ? "text" : "password"}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder={t("passwordReset.newPasswordPlaceholder")}
								className="w-full pr-10"
								autoFocus
								disabled={isLoading}
								required
							/>
							<Button
								variant="plain"
								size="icon"
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground"
								disabled={isLoading}
							>
								{showPassword ? (
									<EyeOff className="h-5 w-5" />
								) : (
									<Eye className="h-5 w-5" />
								)}
							</Button>
						</div>
					</div>

					{/* Confirm Password Input */}
					<div className="space-y-2">
						<label
							className="text-sm text-muted-foreground"
							htmlFor={confirmPasswordId}
						>
							{t("passwordReset.confirmPassword")}
						</label>
						<div className="relative">
							<Input
								id={confirmPasswordId}
								type={showConfirmPassword ? "text" : "password"}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								placeholder={t("passwordReset.confirmPasswordPlaceholder")}
								className="w-full pr-10"
								disabled={isLoading}
								required
							/>
							<Button
								variant="plain"
								size="icon"
								type="button"
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground"
								disabled={isLoading}
							>
								{showConfirmPassword ? (
									<EyeOff className="h-5 w-5" />
								) : (
									<Eye className="h-5 w-5" />
								)}
							</Button>
						</div>
					</div>

					{/* Password Requirements */}
					<div className="text-xs text-muted-foreground space-y-1">
						<p>{t("passwordReset.requirements")}</p>
					</div>

					{/* Submit Button */}
					<Button
						type="submit"
						className="w-full"
						size="lg"
						disabled={isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
								{t("passwordReset.resetting")}
							</>
						) : (
							t("passwordReset.resetButton")
						)}
					</Button>
				</form>

				<div className="text-center">
					<Link
						to="/auth/login"
						className="text-sm text-primary hover:underline"
					>
						{t("passwordReset.backToLogin")}
					</Link>
				</div>

				<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">{t("footer.terms")}</Link>
					<Link to="/datenschutz">{t("footer.privacy")}</Link>
				</div>
			</div>
		</div>
	);
};

export default PasswordResetPage;
